<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  formatTime,
  formatReportDuration,
  durationMetricTooltip,
} from '../constants';
import { useSummaryReportArchive } from '../composables/useSummaryReportArchive';
import { formatPeriodRangeLabel } from '../helpers/dynamicsGranularityHelper';

const props = defineProps({
  type: {
    type: String,
    required: true,
  },
  metrics: {
    type: Array,
    default: () => [],
  },
  withoutTagsCount: {
    type: Number,
    default: 0,
  },
  tagPresence: {
    type: String,
    default: 'all',
  },
  variant: {
    type: String,
    default: 'overview',
    validator: value => ['overview', 'detail'].includes(value),
  },
  archiveContext: {
    type: Object,
    default: () => ({}),
  },
  primaryFrom: {
    type: Number,
    default: 0,
  },
  primaryTo: {
    type: Number,
    default: 0,
  },
  comparisonSlices: {
    type: Array,
    default: () => [],
  },
});

const { t } = useI18n();
const { openArchive, isMetricLinkable, resolveMetricKeyFromCardKey } =
  useSummaryReportArchive();

const formatCount = value => (value ? value.toLocaleString() : '0');
const formatPercent = value =>
  value === null || value === undefined ? '--' : `${Number(value).toFixed(1)}%`;

const sumBy = (metrics, key) =>
  (metrics || []).reduce((total, metric) => total + (metric[key] || 0), 0);

const largestEntry = (metrics, key) => {
  if (!metrics?.length) return null;

  const top = metrics.reduce((best, metric) =>
    (metric[key] || 0) > (best[key] || 0) ? metric : best
  );
  const total = sumBy(metrics, key);
  const share = total ? ((top[key] || 0) / total) * 100 : 0;

  return { id: top.id, name: top.name, count: top[key] || 0, share };
};

const primaryPeriodLabel = computed(() =>
  formatPeriodRangeLabel(props.primaryFrom, props.primaryTo)
);

const comparisonPeriodLabels = computed(() =>
  props.comparisonSlices.map(slice =>
    formatPeriodRangeLabel(slice.from, slice.to)
  )
);

const buildComparisons = ({
  sumKey = null,
  compute = null,
  formatValue = formatCount,
}) => {
  if (!props.comparisonSlices.length) return [];

  return props.comparisonSlices.map((slice, index) => {
    let raw;
    if (compute) {
      raw = compute(slice.data || []);
    } else if (sumKey) {
      raw = sumBy(slice.data || [], sumKey);
    } else {
      raw = null;
    }

    return {
      value: raw === null || raw === undefined ? '--' : formatValue(raw),
      periodLabel: comparisonPeriodLabels.value[index] || '',
    };
  });
};

const defaultCardTooltip = (type, key) => {
  if (type === 'chats') {
    const map = {
      created: 'CREATED',
      resolved: 'RESOLVED',
      reopened: 'REOPENED',
      'with-transfers': 'WITH_TRANSFERS',
      'without-human-response': 'WITHOUT_HUMAN_RESPONSE',
    };
    return map[key] ? t(`CHATS_REPORTS.CARDS.TOOLTIPS.${map[key]}`) : '';
  }

  const groups = {
    agent: {
      active: 'ACTIVE_OPERATORS',
      accepted: 'ACCEPTED_EPISODES',
      answered: 'ANSWERED_EPISODES',
      unanswered: 'UNANSWERED_EPISODES',
      resolved: 'RESOLVED',
      transferred: 'TRANSFERRED',
    },
    team: {
      active: 'ACTIVE_TEAMS',
      episodes: 'EPISODES',
      resolved: 'RESOLVED',
      'transfers-in': 'TRANSFERS_IN',
      'transfers-out': 'TRANSFERS_OUT',
      backlog: 'BACKLOG_END',
    },
    label: {
      tagged: 'WITH_TAGS',
      'without-tags': 'WITHOUT_TAGS',
      'tags-used': 'TAGS_USED',
      'top-tag': 'MOST_FREQUENT_TAG',
      resolved: 'RESOLVED',
      conversations: 'WITH_TAGS',
      backlog: 'WITHOUT_TAGS',
    },
    inbox: {
      active: 'ACTIVE_SOURCES',
      created: 'CREATED',
      resolved: 'RESOLVED',
      backlog: 'BACKLOG_END',
      largest: 'LARGEST_SOURCE',
    },
  };
  const group = type.toUpperCase();
  const mapped = groups[type]?.[key];
  return mapped ? t(`SUMMARY_REPORTS.CARDS.TOOLTIPS.${group}.${mapped}`) : '';
};

const tip = (group, key) => t(`SUMMARY_REPORTS.CARDS.TOOLTIPS.${group}.${key}`);

const buildCard = ({
  key,
  label,
  value,
  metricKey = null,
  row = null,
  rawValue = null,
  comparisons = [],
  tooltip = '',
  seconds = null,
}) => {
  const resolvedMetricKey =
    metricKey || resolveMetricKeyFromCardKey(props.type, key);
  const numericValue =
    rawValue ??
    (resolvedMetricKey ? sumBy(props.metrics, resolvedMetricKey) : 0);
  const hasTimeValue =
    seconds !== null && seconds !== undefined && seconds !== '';

  return {
    key,
    label,
    value,
    metricKey: resolvedMetricKey,
    row,
    comparisons,
    linkable:
      resolvedMetricKey &&
      isMetricLinkable(
        resolvedMetricKey,
        props.type === 'chats' ? 'account' : props.type
      ) &&
      (hasTimeValue || numericValue > 0),
    tooltip: durationMetricTooltip(
      t,
      tooltip || defaultCardTooltip(props.type, key),
      seconds
    ),
  };
};

const onCardClick = card => {
  if (!card.linkable) return;

  const reportType = props.type === 'chats' ? 'account' : props.type;
  let context = props.archiveContext;

  // Chats-only cards: tighten archive filters to match the card definition.
  if (props.type === 'chats' && card.key === 'without-human-response') {
    context = {
      ...context,
      entityFilters: {
        ...(context.entityFilters || {}),
        humanResponse: 'absent',
      },
    };
  } else if (props.type === 'chats' && card.key === 'reopened') {
    context = {
      ...context,
      entityFilters: {
        ...(context.entityFilters || {}),
        reopening: 'only_reopened',
      },
    };
  }

  openArchive({
    metricKey: card.metricKey,
    reportType,
    row: card.row,
    context,
  });
};

const cards = computed(() => {
  const m = props.metrics;

  switch (props.type) {
    case 'agent':
      return [
        buildCard({
          key: 'active',
          label: t('SUMMARY_REPORTS.CARDS.AGENT.ACTIVE_OPERATORS'),
          value: formatCount(
            m.filter(
              metric =>
                (metric.onlineSeconds || 0) > 0 ||
                (metric.uniqueCyclesHandled || 0) > 0
            ).length
          ),
          comparisons: buildComparisons({
            compute: data =>
              data.filter(
                metric =>
                  (metric.onlineSeconds || 0) > 0 ||
                  (metric.uniqueCyclesHandled || 0) > 0
              ).length,
          }),
        }),
        buildCard({
          key: 'accepted',
          label: t('SUMMARY_REPORTS.CARDS.AGENT.ACCEPTED_EPISODES'),
          value: formatCount(
            sumBy(m, 'assignmentEpisodesCount') ||
              sumBy(m, 'uniqueCyclesHandled')
          ),
          metricKey: 'assignmentEpisodesCount',
          comparisons: buildComparisons({
            compute: data =>
              sumBy(data, 'assignmentEpisodesCount') ||
              sumBy(data, 'uniqueCyclesHandled'),
          }),
        }),
        buildCard({
          key: 'answered',
          label: t('SUMMARY_REPORTS.CARDS.AGENT.ANSWERED_EPISODES'),
          value: formatCount(sumBy(m, 'answeredEpisodesCount')),
          comparisons: buildComparisons({ sumKey: 'answeredEpisodesCount' }),
        }),
        buildCard({
          key: 'unanswered',
          label: t('SUMMARY_REPORTS.CARDS.AGENT.UNANSWERED_EPISODES'),
          value: formatCount(sumBy(m, 'unansweredEpisodesCount')),
          comparisons: buildComparisons({ sumKey: 'unansweredEpisodesCount' }),
        }),
        buildCard({
          key: 'resolved',
          label: t('SUMMARY_REPORTS.CARDS.AGENT.RESOLVED'),
          value: formatCount(sumBy(m, 'resolvedConversationsCount')),
          comparisons: buildComparisons({
            sumKey: 'resolvedConversationsCount',
          }),
        }),
        buildCard({
          key: 'transferred',
          label: t('SUMMARY_REPORTS.CARDS.AGENT.TRANSFERRED'),
          value: formatCount(sumBy(m, 'transfersOutCount')),
          comparisons: buildComparisons({ sumKey: 'transfersOutCount' }),
        }),
        ...(props.variant === 'detail' && m[0]
          ? [
              buildCard({
                key: 'median-frt',
                label: t('SUMMARY_REPORTS.MEDIAN_FIRST_RESPONSE_TIME'),
                value: formatReportDuration(
                  Number(m[0].medianOnlineFirstResponseTime) > 0
                    ? m[0].medianOnlineFirstResponseTime
                    : m[0].medianFirstResponseTime
                ),
                row: m[0],
                metricKey:
                  Number(m[0].medianOnlineFirstResponseTime) > 0
                    ? 'answeredEpisodesCount'
                    : 'firstResponseCount',
                seconds:
                  Number(m[0].medianOnlineFirstResponseTime) > 0
                    ? m[0].medianOnlineFirstResponseTime
                    : m[0].medianFirstResponseTime,
              }),
              buildCard({
                key: 'avg-art',
                label: t('SUMMARY_REPORTS.AVG_REPLY_TIME'),
                value: formatReportDuration(m[0].avgReplyTime) || '--',
                row: m[0],
                metricKey: 'replyTimeCount',
                seconds: m[0].avgReplyTime,
              }),
              buildCard({
                key: 'median-assignment',
                label: t('SUMMARY_REPORTS.MEDIAN_ASSIGNMENT_DURATION'),
                value:
                  formatReportDuration(m[0].medianAssignmentDuration) || '--',
                row: m[0],
                seconds: m[0].medianAssignmentDuration,
              }),
            ]
          : []),
      ];
    case 'team':
      return [
        buildCard({
          key: 'active',
          label: t('SUMMARY_REPORTS.CARDS.TEAM.ACTIVE_TEAMS'),
          value: formatCount(
            m.filter(
              metric =>
                (metric.conversationsCount || 0) > 0 ||
                (metric.backlogEnd || 0) > 0
            ).length
          ),
          comparisons: buildComparisons({
            compute: data =>
              data.filter(
                metric =>
                  (metric.conversationsCount || 0) > 0 ||
                  (metric.backlogEnd || 0) > 0
              ).length,
          }),
        }),
        buildCard({
          key: 'episodes',
          label: t('SUMMARY_REPORTS.CARDS.TEAM.EPISODES'),
          value: formatCount(
            sumBy(m, 'assignmentEpisodesCount') ||
              sumBy(m, 'conversationsCount')
          ),
          metricKey: 'assignmentEpisodesCount',
          comparisons: buildComparisons({
            compute: data =>
              sumBy(data, 'assignmentEpisodesCount') ||
              sumBy(data, 'conversationsCount'),
          }),
        }),
        buildCard({
          key: 'resolved',
          label: t('SUMMARY_REPORTS.CARDS.TEAM.RESOLVED'),
          value: formatCount(sumBy(m, 'resolvedConversationsCount')),
          comparisons: buildComparisons({
            sumKey: 'resolvedConversationsCount',
          }),
        }),
        buildCard({
          key: 'transfers-in',
          label: t('SUMMARY_REPORTS.CARDS.TEAM.TRANSFERS_IN'),
          value: formatCount(sumBy(m, 'transfersInCount')),
          metricKey: 'transfersInCount',
          comparisons: buildComparisons({ sumKey: 'transfersInCount' }),
        }),
        buildCard({
          key: 'transfers-out',
          label: t('SUMMARY_REPORTS.CARDS.TEAM.TRANSFERS_OUT'),
          value: formatCount(sumBy(m, 'transfersOutCount')),
          metricKey: 'transfersOutCount',
          comparisons: buildComparisons({ sumKey: 'transfersOutCount' }),
        }),
        buildCard({
          key: 'backlog',
          label: t('SUMMARY_REPORTS.CARDS.TEAM.BACKLOG_END'),
          value: formatCount(sumBy(m, 'backlogEnd')),
          comparisons: buildComparisons({ sumKey: 'backlogEnd' }),
        }),
        ...(props.variant === 'detail' && m[0]
          ? [
              buildCard({
                key: 'median-frt',
                label: t('SUMMARY_REPORTS.MEDIAN_FIRST_RESPONSE_TIME'),
                value:
                  formatReportDuration(m[0].medianFirstResponseTime) || '--',
                row: m[0],
                seconds: m[0].medianFirstResponseTime,
              }),
              buildCard({
                key: 'median-handling',
                label: t('SUMMARY_REPORTS.MEDIAN_TEAM_HANDLING'),
                value:
                  formatReportDuration(m[0].medianTeamHandlingSeconds) || '--',
                row: m[0],
                seconds: m[0].medianTeamHandlingSeconds,
              }),
            ]
          : []),
      ];
    case 'label': {
      if (props.tagPresence === 'without') {
        const metric = m[0] || {};
        return [
          buildCard({
            key: 'conversations',
            label: t('SUMMARY_REPORTS.CONVERSATIONS'),
            value: formatCount(metric.conversationsCount),
            row: { id: 0, name: t('SUMMARY_REPORTS.CARDS.LABEL.WITHOUT_TAGS') },
            rawValue: metric.conversationsCount,
            comparisons: buildComparisons({ sumKey: 'conversationsCount' }),
          }),
          buildCard({
            key: 'resolved',
            label: t('SUMMARY_REPORTS.RESOLUTION_COUNT'),
            value: formatCount(metric.resolvedConversationsCount),
            row: { id: 0, name: t('SUMMARY_REPORTS.CARDS.LABEL.WITHOUT_TAGS') },
            rawValue: metric.resolvedConversationsCount,
            comparisons: buildComparisons({
              sumKey: 'resolvedConversationsCount',
            }),
          }),
          buildCard({
            key: 'backlog',
            label: t('SUMMARY_REPORTS.BACKLOG_END'),
            value: formatCount(metric.backlogEnd),
            row: { id: 0, name: t('SUMMARY_REPORTS.CARDS.LABEL.WITHOUT_TAGS') },
            rawValue: metric.backlogEnd,
            comparisons: buildComparisons({ sumKey: 'backlogEnd' }),
          }),
          buildCard({
            key: 'bot-rate',
            label: t('SUMMARY_REPORTS.BOT_RESOLUTION_RATE'),
            value: formatPercent(metric.botResolutionRate),
            comparisons: buildComparisons({
              compute: data => data[0]?.botResolutionRate,
              formatValue: formatPercent,
            }),
          }),
          buildCard({
            key: 'median-frt',
            label: t('SUMMARY_REPORTS.MEDIAN_FIRST_RESPONSE_TIME'),
            value: formatTime(metric.medianFirstResponseTime) || '--',
            row: { id: 0, name: t('SUMMARY_REPORTS.CARDS.LABEL.WITHOUT_TAGS') },
            seconds: metric.medianFirstResponseTime,
            comparisons: buildComparisons({
              compute: data => data[0]?.medianFirstResponseTime,
              formatValue: v => formatTime(v) || '--',
            }),
          }),
        ];
      }

      if (props.variant === 'detail' && m.length === 1) {
        const metric = m[0];
        return [
          buildCard({
            key: 'conversations',
            label: t('SUMMARY_REPORTS.CONVERSATIONS'),
            value: formatCount(metric.conversationsCount),
            row: metric,
            rawValue: metric.conversationsCount,
            comparisons: buildComparisons({ sumKey: 'conversationsCount' }),
          }),
          buildCard({
            key: 'resolved',
            label: t('SUMMARY_REPORTS.RESOLUTION_COUNT'),
            value: formatCount(metric.resolvedConversationsCount),
            row: metric,
            rawValue: metric.resolvedConversationsCount,
            comparisons: buildComparisons({
              sumKey: 'resolvedConversationsCount',
            }),
          }),
          buildCard({
            key: 'backlog',
            label: t('SUMMARY_REPORTS.BACKLOG_END'),
            value: formatCount(metric.backlogEnd),
            row: metric,
            rawValue: metric.backlogEnd,
            comparisons: buildComparisons({ sumKey: 'backlogEnd' }),
          }),
          buildCard({
            key: 'bot-rate',
            label: t('SUMMARY_REPORTS.BOT_RESOLUTION_RATE'),
            value: formatPercent(metric.botResolutionRate),
            comparisons: buildComparisons({
              compute: data => data[0]?.botResolutionRate,
              formatValue: formatPercent,
            }),
          }),
          buildCard({
            key: 'tag-share',
            label: t('SUMMARY_REPORTS.TAG_SHARE'),
            value: formatPercent(metric.tagShare),
            comparisons: buildComparisons({
              compute: data => data[0]?.tagShare,
              formatValue: formatPercent,
            }),
          }),
          buildCard({
            key: 'median-frt',
            label: t('SUMMARY_REPORTS.MEDIAN_FIRST_RESPONSE_TIME'),
            value: formatTime(metric.medianFirstResponseTime) || '--',
            row: metric,
            seconds: metric.medianFirstResponseTime,
            comparisons: buildComparisons({
              compute: data => data[0]?.medianFirstResponseTime,
              formatValue: v => formatTime(v) || '--',
            }),
          }),
        ];
      }

      const usedTags = m.filter(metric => (metric.conversationsCount || 0) > 0);
      const topTag = largestEntry(m, 'conversationsCount');

      return [
        buildCard({
          key: 'tagged',
          label: t('SUMMARY_REPORTS.CARDS.LABEL.WITH_TAGS'),
          value: formatCount(sumBy(m, 'conversationsCount')),
          comparisons: buildComparisons({ sumKey: 'conversationsCount' }),
        }),
        buildCard({
          key: 'without-tags',
          label: t('SUMMARY_REPORTS.CARDS.LABEL.WITHOUT_TAGS'),
          value: formatCount(props.withoutTagsCount),
          row: { id: 0, name: t('SUMMARY_REPORTS.CARDS.LABEL.WITHOUT_TAGS') },
          rawValue: props.withoutTagsCount,
        }),
        buildCard({
          key: 'tags-used',
          label: t('SUMMARY_REPORTS.CARDS.LABEL.TAGS_USED'),
          value: formatCount(usedTags.length),
          comparisons: buildComparisons({
            compute: data =>
              data.filter(metric => (metric.conversationsCount || 0) > 0)
                .length,
          }),
        }),
        buildCard({
          key: 'top-tag',
          label: t('SUMMARY_REPORTS.CARDS.LABEL.MOST_FREQUENT_TAG'),
          value: topTag
            ? `${topTag.name} (${formatCount(topTag.count)})`
            : '--',
          metricKey: 'conversationsCount',
          row: topTag ? { id: topTag.id, name: topTag.name } : null,
          rawValue: topTag?.count || 0,
          comparisons: buildComparisons({
            compute: data => {
              const top = largestEntry(data, 'conversationsCount');
              return top ? `${top.name} (${formatCount(top.count)})` : '--';
            },
            formatValue: v => v,
          }),
        }),
        buildCard({
          key: 'resolved',
          label: t('SUMMARY_REPORTS.CARDS.LABEL.RESOLVED'),
          value: formatCount(sumBy(m, 'resolvedConversationsCount')),
          comparisons: buildComparisons({
            sumKey: 'resolvedConversationsCount',
          }),
        }),
      ];
    }
    case 'inbox': {
      const largest = largestEntry(m, 'conversationsCount');

      return [
        buildCard({
          key: 'active',
          label: t('SUMMARY_REPORTS.CARDS.INBOX.ACTIVE_SOURCES'),
          value: formatCount(
            m.filter(
              metric =>
                (metric.conversationsCount || 0) > 0 ||
                (metric.backlogEnd || 0) > 0
            ).length
          ),
          comparisons: buildComparisons({
            compute: data =>
              data.filter(
                metric =>
                  (metric.conversationsCount || 0) > 0 ||
                  (metric.backlogEnd || 0) > 0
              ).length,
          }),
        }),
        buildCard({
          key: 'created',
          label: t('SUMMARY_REPORTS.CARDS.INBOX.CREATED'),
          value: formatCount(sumBy(m, 'conversationsCount')),
          comparisons: buildComparisons({ sumKey: 'conversationsCount' }),
        }),
        buildCard({
          key: 'resolved',
          label: t('SUMMARY_REPORTS.CARDS.INBOX.RESOLVED'),
          value: formatCount(sumBy(m, 'resolvedConversationsCount')),
          comparisons: buildComparisons({
            sumKey: 'resolvedConversationsCount',
          }),
        }),
        buildCard({
          key: 'backlog',
          label: t('SUMMARY_REPORTS.CARDS.INBOX.BACKLOG_END'),
          value: formatCount(sumBy(m, 'backlogEnd')),
          comparisons: buildComparisons({ sumKey: 'backlogEnd' }),
        }),
        buildCard({
          key: 'largest',
          label: t('SUMMARY_REPORTS.CARDS.INBOX.LARGEST_SOURCE'),
          value: largest
            ? `${largest.name} (${formatPercent(largest.share)})`
            : '--',
          metricKey: 'conversationsCount',
          row: largest ? { id: largest.id, name: largest.name } : null,
          rawValue: largest?.count || 0,
          comparisons: buildComparisons({
            compute: data => {
              const top = largestEntry(data, 'conversationsCount');
              return top ? `${top.name} (${formatPercent(top.share)})` : '--';
            },
            formatValue: v => v,
          }),
        }),
      ];
    }
    case 'account': {
      const metric = m[0] || {};
      const botValue =
        metric.botResolvedCycles || metric.botResolvedCycles === 0
          ? `${formatCount(metric.botResolvedCycles)} (${formatPercent(
              metric.botResolutionRate
            )})`
          : '--';

      return [
        buildCard({
          key: 'created',
          label: t('SUMMARY_REPORTS.CARDS.ACCOUNT.CREATED'),
          value: formatCount(metric.createdCycles),
          metricKey: 'conversationsCount',
          rawValue: metric.createdCycles,
          tooltip: tip('ACCOUNT', 'CREATED'),
          comparisons: buildComparisons({
            compute: data => data[0]?.createdCycles,
          }),
        }),
        buildCard({
          key: 'resolved',
          label: t('SUMMARY_REPORTS.CARDS.ACCOUNT.RESOLVED'),
          value: formatCount(metric.resolvedCycles),
          metricKey: 'resolutionsCount',
          rawValue: metric.resolvedCycles,
          tooltip: tip('ACCOUNT', 'RESOLVED'),
          comparisons: buildComparisons({
            compute: data => data[0]?.resolvedCycles,
          }),
        }),
        buildCard({
          key: 'reopened',
          label: t('SUMMARY_REPORTS.CARDS.ACCOUNT.REOPENED'),
          value: formatCount(metric.reopenedCycles),
          metricKey: 'reopenedCount',
          rawValue: metric.reopenedCycles,
          tooltip: tip('ACCOUNT', 'REOPENED'),
          comparisons: buildComparisons({
            compute: data => data[0]?.reopenedCycles,
          }),
        }),
        buildCard({
          key: 'bot-resolved',
          label: t('SUMMARY_REPORTS.CARDS.ACCOUNT.BOT_RESOLVED'),
          value: botValue,
          metricKey: 'botResolvedCount',
          rawValue: metric.botResolvedCycles,
          tooltip: tip('ACCOUNT', 'BOT_RESOLVED'),
          comparisons: buildComparisons({
            compute: data => {
              const row = data[0] || {};
              if (row.botResolvedCycles || row.botResolvedCycles === 0) {
                return `${formatCount(row.botResolvedCycles)} (${formatPercent(
                  row.botResolutionRate
                )})`;
              }
              return '--';
            },
            formatValue: v => v,
          }),
        }),
        buildCard({
          key: 'median-frt',
          label: t('SUMMARY_REPORTS.CARDS.ACCOUNT.MEDIAN_HUMAN_FRT'),
          value:
            formatReportDuration(metric.medianFirstHumanResponseSeconds) ||
            '--',
          rawValue: metric.medianFirstHumanResponseSeconds ? 1 : 0,
          tooltip: tip('ACCOUNT', 'MEDIAN_HUMAN_FRT'),
          seconds: metric.medianFirstHumanResponseSeconds,
          comparisons: buildComparisons({
            compute: data => data[0]?.medianFirstHumanResponseSeconds,
            formatValue: v => formatReportDuration(v) || '--',
          }),
        }),
        buildCard({
          key: 'median-resolution',
          label: t('SUMMARY_REPORTS.CARDS.ACCOUNT.MEDIAN_RESOLUTION'),
          value: formatReportDuration(metric.medianResolutionSeconds) || '--',
          rawValue: metric.resolvedCycles,
          tooltip: tip('ACCOUNT', 'MEDIAN_RESOLUTION'),
          seconds: metric.medianResolutionSeconds,
          comparisons: buildComparisons({
            compute: data => data[0]?.medianResolutionSeconds,
            formatValue: v => formatReportDuration(v) || '--',
          }),
        }),
        buildCard({
          key: 'csat',
          label: t('SUMMARY_REPORTS.CARDS.ACCOUNT.CSAT'),
          value:
            metric.csatAverage != null
              ? `${Number(metric.csatAverage).toFixed(1)}/${
                  metric.csatResponses || 0
                }`
              : '--',
          rawValue: metric.csatResponses || 0,
          tooltip: tip('ACCOUNT', 'CSAT'),
          comparisons: buildComparisons({
            compute: data => {
              const row = data[0] || {};
              return row.csatAverage != null
                ? `${Number(row.csatAverage).toFixed(1)}/${
                    row.csatResponses || 0
                  }`
                : '--';
            },
            formatValue: v => v,
          }),
        }),
      ];
    }
    case 'chats': {
      const metric = m[0] || {};
      return [
        buildCard({
          key: 'created',
          label: t('CHATS_REPORTS.CARDS.CREATED'),
          value: formatCount(metric.createdCycles),
          metricKey: 'conversationsCount',
          rawValue: metric.createdCycles,
          comparisons: buildComparisons({
            compute: data => data[0]?.createdCycles,
          }),
        }),
        buildCard({
          key: 'resolved',
          label: t('CHATS_REPORTS.CARDS.RESOLVED'),
          value: formatCount(metric.resolvedCycles),
          metricKey: 'resolutionsCount',
          rawValue: metric.resolvedCycles,
          comparisons: buildComparisons({
            compute: data => data[0]?.resolvedCycles,
          }),
        }),
        buildCard({
          key: 'reopened',
          label: t('CHATS_REPORTS.CARDS.REOPENED'),
          value: formatCount(metric.reopenedCycles),
          metricKey: 'reopenedCount',
          rawValue: metric.reopenedCycles,
          comparisons: buildComparisons({
            compute: data => data[0]?.reopenedCycles,
          }),
        }),
        buildCard({
          key: 'with-transfers',
          label: t('CHATS_REPORTS.CARDS.WITH_TRANSFERS'),
          value: formatCount(metric.withTransfersCount),
          metricKey: 'transfersOutCount',
          rawValue: metric.withTransfersCount,
          comparisons: buildComparisons({
            compute: data => data[0]?.withTransfersCount,
          }),
        }),
        buildCard({
          key: 'without-human-response',
          label: t('CHATS_REPORTS.CARDS.WITHOUT_HUMAN_RESPONSE'),
          value: formatCount(metric.withoutHumanResponseCount),
          metricKey: 'conversationsCount',
          rawValue: metric.withoutHumanResponseCount,
          comparisons: buildComparisons({
            compute: data => data[0]?.withoutHumanResponseCount,
          }),
        }),
      ];
    }
    default:
      return [];
  }
});

const showPeriodLabels = computed(
  () => props.comparisonSlices.length > 0 && !!primaryPeriodLabel.value
);
</script>

<template>
  <div
    v-if="cards.length"
    class="grid gap-4 mt-4"
    :class="{
      'grid-cols-2 md:grid-cols-3 xl:grid-cols-6': type === 'agent',
      'grid-cols-2 md:grid-cols-3 xl:grid-cols-5':
        type === 'inbox' || type === 'label' || type === 'chats',
      'grid-cols-2 md:grid-cols-4': type === 'team',
      'grid-cols-2 md:grid-cols-4 xl:grid-cols-7': type === 'account',
    }"
  >
    <component
      :is="card.linkable ? 'button' : 'div'"
      v-for="card in cards"
      :key="card.key"
      type="button"
      class="rounded-xl border border-n-container bg-n-solid-2 px-4 py-4 shadow outline-1 outline outline-n-container text-left"
      :class="{
        'cursor-pointer hover:border-n-brand hover:bg-n-alpha-1 transition-colors':
          card.linkable,
        'last:col-span-2 xl:last:col-span-1':
          type === 'account' || type === 'chats',
      }"
      :title="card.linkable ? $t('SUMMARY_REPORTS.ARCHIVE_LINK.TOOLTIP') : null"
      @click="card.linkable ? onCardClick(card) : undefined"
    >
      <p class="text-sm text-n-slate-11 inline-flex items-center gap-1">
        {{ card.label }}
        <span
          v-if="card.tooltip"
          v-tooltip.right="card.tooltip"
          class="i-lucide-info flex-shrink-0 text-n-slate-10 size-3.5"
        />
      </p>
      <p
        class="mt-2 text-2xl font-semibold break-words"
        :class="card.linkable ? 'text-n-brand' : 'text-n-slate-12'"
      >
        {{ card.value }}
      </p>
      <p
        v-if="showPeriodLabels"
        class="mt-1 text-[11px] leading-tight text-n-slate-10"
      >
        {{ primaryPeriodLabel }}
      </p>
      <div
        v-for="(cmp, idx) in card.comparisons"
        :key="`${card.key}-cmp-${idx}`"
        class="mt-2 border-t border-n-weak pt-2"
      >
        <p class="text-sm font-medium text-n-slate-11 tabular-nums">
          {{ cmp.value }}
        </p>
        <p
          v-if="cmp.periodLabel"
          class="mt-0.5 text-[11px] leading-tight text-n-slate-10"
        >
          {{ cmp.periodLabel }}
        </p>
      </div>
    </component>
  </div>
</template>
